import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import loadImage from '../assets/imageLoader';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import { 
    FaEdit, FaSave, FaTimes, FaCamera, FaMapMarkerAlt, FaPhoneAlt, 
    FaEnvelope, FaRulerCombined, FaWeightHanging, FaDumbbell, 
    FaBriefcase, FaMoneyBillWave, FaCheckCircle, FaStar, FaClock 
} from 'react-icons/fa';
import { Map, MapMarker, MapControls } from '../components/ui/map';

function WorkerProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [worker, setWorker] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [rating, setRating] = useState('New');
  const [receivedRatings, setReceivedRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({});
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // New File States
  const [idDocument, setIdDocument] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [expCert, setExpCert] = useState(null);

  const [mapPosition, setMapPosition] = useState(null);

  // Dropdowns
  const [categories, setCategories] = useState([]);

  const isOwnProfile = !id || (user?.userId && user.userId === id);
  const userIdToFetch = id || user?.userId;

  // --- Fetch Logic ---
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const catResponse = await apiClient.get('/categories');
        setCategories(catResponse.data || []);
      } catch (err) {
        console.error("Failed to load dropdown data", err);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchWorker = async () => {
      if (!userIdToFetch) return;
      try {
        setLoading(true);
        const [profileRes, ratingRes] = await Promise.all([
            apiClient.get(`/profiles/${userIdToFetch}`),
            apiClient.get(`/profiles/${userIdToFetch}/ratings`)
        ]);
        
        const workerData = profileRes.data;
        const ratings = ratingRes.data || [];
        setReceivedRatings(ratings);

        // Calculate Rating
        if (ratings.length > 0) {
            const avg = ratings.reduce((acc, r) => acc + r.rating, 0) / ratings.length;
            setRating(avg.toFixed(1));
        } else {
            setRating('New');
        }
        
        if (isOwnProfile) {
          try {
            const walletRes = await apiClient.get('/wallet');
            setWallet(walletRes.data);
          } catch (wErr) { console.error(wErr); }
        }

        const categoryNames = workerData.worker_details?.categories?.map(c => c.Name) || [];
        setWorker({
          ...workerData,
          worker_details: { ...workerData.worker_details, preferred_working_category: categoryNames }
        });

        setFormData({
          name: workerData.name,
          email: workerData.User?.email || '',
          date_of_birth: workerData.date_of_birth ? new Date(workerData.date_of_birth).toISOString().split('T')[0] : '',
          gender: workerData.gender,
          nid_or_passport: workerData.nid_or_passport,
          division: workerData.location?.division || '',
          district: workerData.location?.district || '',
          upazila: workerData.location?.upazila || '',
          granular_location: workerData.location?.granular_location || '',
          height: workerData.worker_details?.height || '',
          weight: workerData.worker_details?.weight || '',
          strength: workerData.worker_details?.strength || '',
          expected_wage: workerData.worker_details?.expected_wage || '',
          preferred_working_time: workerData.worker_details?.preferred_working_time || '',
          payout_method: workerData.worker_details?.payout_method || '',
          personal_cv: workerData.worker_details?.personal_cv || '',
          preferred_working_category: categoryNames,
        });
        
        if (workerData.location?.lat && workerData.location?.lng) {
            setMapPosition({ lat: workerData.location.lat, lng: workerData.location.lng });
        } else {
            setMapPosition({ lat: 23.8103, lng: 90.4125 }); // Default Dhaka
        }

        setPreviewImage(loadImage(workerData.picture));
      } catch (err) {
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };
    fetchWorker();
  }, [userIdToFetch, isOwnProfile]);

  // --- Handlers ---
  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
        setUploadedImage(e.target.files[0]);
        setPreviewImage(URL.createObjectURL(e.target.files[0]));
    }
  };
  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
        ...prev,
        preferred_working_category: checked 
            ? [...(prev.preferred_working_category || []), value]
            : (prev.preferred_working_category || []).filter(c => c !== value)
    }));
  };

  const handleMapClick = async (coords) => {
      setMapPosition(coords);
      try {
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}`);
          const data = await response.json();
          if (data && data.address) {
              setFormData(prev => ({
                  ...prev,
                  division: data.address.state || data.address.region || prev.division,
                  district: data.address.city || data.address.county || prev.district,
                  upazila: data.address.suburb || data.address.town || prev.upazila,
                  granular_location: data.display_name
              }));
          }
      } catch (err) {
          console.error("Reverse geocoding failed", err);
      }
  };

  const handleGeolocate = (coords) => {
      setMapPosition(coords);
      handleMapClick(coords);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const payload = new FormData();
      
      // 1. Profile Data
      const profileObj = {
        name: formData.name,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        nid_or_passport: formData.nid_or_passport,
        location: { 
            division: formData.division, 
            district: formData.district, 
            upazila: formData.upazila, 
            granular_location: formData.granular_location,
            lat: mapPosition?.lat,
            lng: mapPosition?.lng
        }
      };
      payload.append('profile', JSON.stringify(profileObj));

      // 2. Worker Data
      const workerObj = {
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        strength: parseFloat(formData.strength),
        expected_wage: parseFloat(formData.expected_wage),
        preferred_working_time: formData.preferred_working_time,
        availability: formData.availability,
        preferred_working_category: formData.preferred_working_category
      };
      payload.append('worker', JSON.stringify(workerObj));

      // 3. Files
      if (uploadedImage) payload.append('profilePicture', uploadedImage);
      if (idDocument) payload.append('id_document', idDocument);
      if (cvFile) payload.append('cv_file', cvFile);
      if (expCert) payload.append('experience_cert', expCert);

      if (!worker.User?.email && formData.email) payload.append('email', formData.email);

      await apiClient.put('/profiles/complete', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
      });
      window.location.reload(); 
    } catch (err) {
      alert("Failed to save profile.");
    } finally {
      setLoading(false);
    }
  };

  const MapModal = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-white w-full max-w-4xl h-[80vh] rounded-3xl overflow-hidden relative shadow-2xl flex flex-col">
            <div className="absolute top-4 left-4 right-4 z-10 bg-white/95 backdrop-blur-md p-4 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-center">
                <div>
                    <p className="text-[10px] font-black uppercase text-emerald-600 tracking-wider">Selected Location</p>
                    <p className="text-sm font-bold text-slate-800 line-clamp-1">{formData.granular_location || "Tap on map..."}</p>
                </div>
                <button 
                    onClick={() => setShowMapModal(false)}
                    className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:bg-emerald-700 shadow-lg shadow-emerald-200"
                >
                    Confirm
                </button>
            </div>
            <div className="flex-grow relative bg-slate-100 mt-20 rounded-b-3xl overflow-hidden">
                <Map 
                    center={mapPosition ? [mapPosition.lng, mapPosition.lat] : [90.4125, 23.8103]} 
                    zoom={13} 
                    onClick={handleMapClick}
                    className="h-full w-full" 
                >
                    <MapControls showLocate onGeolocate={handleGeolocate} showZoom />
                    <MapMarker 
                        longitude={mapPosition?.lng || 90.4125} 
                        latitude={mapPosition?.lat || 23.8103} 
                        draggable 
                        onDragEnd={handleMapClick} 
                    />
                </Map>
            </div>
            <button 
                onClick={() => setShowMapModal(false)} 
                className="absolute top-4 right-4 z-20 bg-white text-slate-800 p-2 rounded-full shadow-md hover:bg-slate-100 md:hidden"
            >
                <FaTimes />
            </button>
        </div>
    </div>
  );

  if (loading) return <div className="flex justify-center items-center h-screen bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div></div>;
  if (error) return <div className="p-10 text-center text-red-500">{error}</div>;

  // --- Components ---
  const SectionTitle = ({ title }) => (
      <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 border-b border-slate-100 pb-2">{title}</h3>
  );

  return (
    <div className="min-h-screen bg-slate-100 font-sans pb-20">
        {showMapModal && <MapModal />}
        
        {/* --- Hero Section --- */}
        <div className="bg-slate-900 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
            
            <div className="max-w-5xl mx-auto px-6 pt-20 pb-32 relative z-10 flex flex-col items-center">
                <div className="relative group mb-6">
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-500"></div>
                    <img 
                        src={previewImage || loadImage(worker.picture)} 
                        alt="Profile" 
                        className="relative w-40 h-40 rounded-full object-cover border-4 border-slate-900 bg-slate-800 shadow-2xl"
                    />
                    {isEditing && (
                        <label className="absolute bottom-2 right-2 bg-emerald-600 text-white p-3 rounded-full cursor-pointer hover:bg-emerald-500 shadow-lg transition-transform hover:scale-110">
                            <FaCamera />
                            <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
                        </label>
                    )}
                </div>
                
                {isEditing ? (
                    <input 
                        value={formData.name} 
                        name="name" 
                        onChange={handleInputChange} 
                        className="text-4xl font-black text-center bg-transparent border-b-2 border-emerald-500 outline-none text-white placeholder-slate-500 w-full max-w-md"
                        placeholder="Your Name"
                    />
                ) : (
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-2 text-center">{worker.name}</h1>
                )}
                
                <div className="flex items-center space-x-2 text-slate-400 mt-2 font-medium bg-slate-800/50 px-4 py-1.5 rounded-full backdrop-blur-sm border border-slate-700">
                    <FaMapMarkerAlt className="text-emerald-500" />
                    {isEditing ? (
                        <button onClick={() => setShowMapModal(true)} className="flex items-center gap-2 hover:text-white transition-colors">
                            <span className="truncate max-w-[200px]">{formData.granular_location || formData.district || 'Set Location'}</span>
                            <FaEdit size={12} />
                        </button>
                    ) : (
                        <span>{worker.location?.district || 'Unknown Location'}, Bangladesh</span>
                    )}
                </div>
            </div>
        </div>

        {/* --- Main Content --- */}
        <div className="max-w-5xl mx-auto px-6 -mt-20 relative z-20">
            <div className="bg-white rounded-[2.5rem] shadow-xl overflow-hidden border border-slate-200">
                
                {/* Stats Bar */}
                <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100 border-b border-slate-100">
                    <div className="p-6 text-center hover:bg-slate-50 transition-colors">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Rating</p>
                        <div className="flex justify-center items-center gap-1">
                            <span className="text-2xl font-black text-slate-800">{rating}</span>
                            <FaStar className="text-yellow-400" />
                        </div>
                    </div>
                    <div className="p-6 text-center hover:bg-slate-50 transition-colors">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Reliability</p>
                        <p className="text-2xl font-black text-emerald-600">{worker.worker_details?.reliability_score || 100}%</p>
                    </div>
                    <div className="p-6 text-center hover:bg-slate-50 transition-colors">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Experience</p>
                        <p className="text-2xl font-black text-slate-800">{worker.worker_details?.experience_year || 0} <span className="text-sm font-medium text-slate-400">Yrs</span></p>
                    </div>
                    <div className="p-6 text-center hover:bg-slate-50 transition-colors">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Wage</p>
                        <p className="text-2xl font-black text-slate-800">৳{worker.worker_details?.expected_wage || 0}</p>
                    </div>
                    <div className="p-6 text-center hover:bg-slate-50 transition-colors">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Status</p>
                        {worker.User?.is_Identity_Verified ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                <FaCheckCircle className="mr-1" /> Verified
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">
                                <FaClock className="mr-1" /> Pending Verification
                            </span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3">
                    
                    {/* Sidebar Info */}
                    <div className="p-8 lg:p-10 border-r border-slate-100 bg-slate-50/50">
                        <SectionTitle title="Contact & Info" />
                        
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <div className="mt-1 mr-4 text-slate-400"><FaPhoneAlt /></div>
                                <div>
                                    <p className="text-xs font-bold text-slate-400 uppercase">Phone</p>
                                    <p className="font-bold text-slate-700">{worker.User?.contact_No}</p>
                                </div>
                            </div>
                            
                            <div className="flex items-start">
                                <div className="mt-1 mr-4 text-slate-400"><FaEnvelope /></div>
                                <div>
                                    <p className="text-xs font-bold text-slate-400 uppercase">Email</p>
                                    {isEditing ? (
                                        <input name="email" value={formData.email} onChange={handleInputChange} className="w-full text-sm border rounded p-1" />
                                    ) : (
                                        <p className="font-bold text-slate-700 break-all">{worker.User?.email || 'N/A'}</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-start">
                                <div className="mt-1 mr-4 text-slate-400"><FaClock /></div>
                                <div>
                                    <p className="text-xs font-bold text-slate-400 uppercase">Availability</p>
                                    {isEditing ? (
                                        <input name="preferred_working_time" value={formData.preferred_working_time} onChange={handleInputChange} className="w-full text-sm border rounded p-1" />
                                    ) : (
                                        <p className="font-bold text-slate-700">{worker.worker_details?.preferred_working_time || 'Flexible'}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Physical Stats in Sidebar */}
                        <div className="mt-10">
                            <SectionTitle title="Physical Stats" />
                            <div className="grid grid-cols-2 gap-3">
                                {isEditing ? (
                                    <>
                                        <input type="number" name="height" placeholder="Height" value={formData.height} onChange={handleInputChange} className="border p-2 rounded text-sm col-span-2"/>
                                        <input type="number" name="weight" placeholder="Weight" value={formData.weight} onChange={handleInputChange} className="border p-2 rounded text-sm"/>
                                        <input type="number" name="strength" placeholder="Strength" value={formData.strength} onChange={handleInputChange} className="border p-2 rounded text-sm"/>
                                    </>
                                ) : (
                                    <>
                                        <div className="bg-white p-3 rounded-xl border border-slate-200 text-center">
                                            <FaRulerCombined className="mx-auto text-slate-300 mb-1"/>
                                            <span className="font-bold text-slate-700">{worker.worker_details?.height || '-'}</span>
                                        </div>
                                        <div className="bg-white p-3 rounded-xl border border-slate-200 text-center">
                                            <FaWeightHanging className="mx-auto text-slate-300 mb-1"/>
                                            <span className="font-bold text-slate-700">{worker.worker_details?.weight || '-'} kg</span>
                                        </div>
                                        <div className="bg-white p-3 rounded-xl border border-slate-200 text-center col-span-2">
                                            <FaDumbbell className="mx-auto text-slate-300 mb-1"/>
                                            <span className="font-bold text-slate-700">{worker.worker_details?.strength || '-'} kg Lift</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="lg:col-span-2 p-8 lg:p-10">
                        <div className="mb-10">
                            <SectionTitle title="Expertise & Skills" />
                            {isEditing ? (
                                <div className="space-y-6 border p-6 rounded-3xl max-h-96 overflow-y-auto bg-slate-50 custom-scrollbar shadow-inner">
                                    {categories.filter(c => !c.parentId).map(industry => (
                                        <div key={industry.id} className="space-y-3">
                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                                <span className="text-sm">{industry.Icon}</span> {industry.Name}
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {categories.filter(c => c.parentId === industry.id).map(service => (
                                                    <label key={service.id} className={`flex items-center space-x-3 p-3 rounded-2xl cursor-pointer transition-all border ${formData.preferred_working_category?.includes(service.Name) ? 'bg-emerald-600 border-emerald-500 text-white shadow-md' : 'bg-white border-slate-100 text-slate-600 hover:border-emerald-200'}`}>
                                                        <input 
                                                            type="checkbox" 
                                                            value={service.Name} 
                                                            checked={formData.preferred_working_category?.includes(service.Name)} 
                                                            onChange={handleCategoryChange} 
                                                            className="hidden" 
                                                        />
                                                        <span className="text-sm font-bold">{service.Name}</span>
                                                        {formData.preferred_working_category?.includes(service.Name) && <FaCheckCircle className="ml-auto" size={12}/>}
                                                    </label>
                                                ))}
                                            </div>
                                            <div className="h-px bg-slate-200/50 my-4 last:hidden"></div>
                                        </div>
                                    ))}
                                    {categories.length === 0 && <p className="text-center text-slate-400 py-10 italic">No categories found.</p>}
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-3">
                                    {worker.worker_details?.preferred_working_category?.map((cat, i) => (
                                        <span key={i} className="px-5 py-2.5 rounded-xl bg-slate-800 text-white font-bold text-sm shadow-md shadow-slate-200 hover:scale-105 transition-transform cursor-default">
                                            {cat}
                                        </span>
                                    ))}
                                    {(!worker.worker_details?.preferred_working_category || worker.worker_details?.preferred_working_category.length === 0) && (
                                        <p className="text-slate-400 italic">No skills listed yet.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <SectionTitle title="About & Terms" />
                            {isEditing ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 mb-1">Expected Wage</label>
                                            <input type="number" name="expected_wage" value={formData.expected_wage} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 mb-1">Current Availability</label>
                                            <select 
                                                name="availability" 
                                                value={formData.availability} 
                                                onChange={handleInputChange} 
                                                className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50"
                                            >
                                                <option value="available">Available</option>
                                                <option value="busy">Busy / On Job</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-slate-100">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Update Documents</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500 ml-1">Identity Card (NID)</label>
                                                <input type="file" onChange={e => setIdDocument(e.target.files[0])} className="w-full text-xs file:bg-emerald-50 file:text-emerald-700 file:border-0 file:rounded-lg file:px-3 file:py-2" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500 ml-1">Updated CV</label>
                                                <input type="file" onChange={e => setCvFile(e.target.files[0])} className="w-full text-xs file:bg-blue-50 file:text-blue-700 file:border-0 file:rounded-lg file:px-3 file:py-2" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500 ml-1">Exp. Certificate</label>
                                                <input type="file" onChange={e => setExpCert(e.target.files[0])} className="w-full text-xs file:bg-purple-50 file:text-purple-700 file:border-0 file:rounded-lg file:px-3 file:py-2" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100 text-emerald-900">
                                    <p className="leading-relaxed font-medium">
                                        Open to work with a preferred daily wage of <span className="font-black">৳{worker.worker_details?.expected_wage || 0}</span>. 
                                        Currently marked as <strong className="uppercase">{worker.worker_details?.availability || 'available'}</strong>.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="mt-10">
                            <SectionTitle title="Documents & Credentials" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {worker.id_document_url && (
                                    <a href={loadImage(worker.id_document_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all group">
                                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-emerald-600 shadow-sm group-hover:scale-110 transition-transform">
                                            <FaBriefcase size={20} />
                                        </div>
                                        <div className="ml-4 overflow-hidden">
                                            <p className="text-sm font-bold text-slate-700 truncate">ID Document</p>
                                            <p className={`text-[10px] font-black uppercase tracking-wider ${worker.User?.is_Identity_Verified ? 'text-emerald-500' : 'text-orange-500'}`}>
                                                {worker.User?.is_Identity_Verified ? 'Verified' : 'Pending Verification'}
                                            </p>
                                        </div>
                                    </a>
                                )}
                                {worker.worker_details?.personal_cv && (
                                    <a href={loadImage(worker.worker_details.personal_cv)} target="_blank" rel="noopener noreferrer" className="flex items-center p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group">
                                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-blue-600 shadow-sm group-hover:scale-110 transition-transform">
                                            <FaBriefcase size={20} />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-bold text-slate-700">Curriculum Vitae</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Resume</p>
                                        </div>
                                    </a>
                                )}
                                {worker.worker_details?.experience_certificate_url && (
                                    <a href={loadImage(worker.worker_details.experience_certificate_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all group">
                                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                                            <FaStar size={20} />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-bold text-slate-700">Experience Cert.</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Proof of Work</p>
                                        </div>
                                    </a>
                                )}
                                {!worker.id_document_url && !worker.worker_details?.personal_cv && !worker.worker_details?.experience_certificate_url && (
                                    <p className="text-slate-400 italic text-sm col-span-2">No public documents available.</p>
                                )}
                            </div>
                        </div>

                        {/* Ratings & Reviews Section */}
                        <div className="pt-10 mt-10 border-t border-slate-100">
                            <SectionTitle title="Reviews & Feedback" />
                            <div className="space-y-4 mt-6">
                                {receivedRatings?.length > 0 ? (
                                    receivedRatings.map((rating) => (
                                        <div key={rating.id} className="bg-white p-6 rounded-3xl border border-slate-50 shadow-sm hover:shadow-md transition-all">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-3">
                                                    <img 
                                                        src={loadImage(rating.rater?.picture)} 
                                                        className="w-10 h-10 rounded-xl object-cover border border-slate-100" 
                                                        alt="Rater" 
                                                    />
                                                    <div>
                                                        <p className="text-sm font-black text-slate-800">{rating.rater?.name || 'Customer'}</p>
                                                        <p className="text-[10px] text-slate-400 font-bold uppercase">{new Date(rating.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                <div className="flex text-yellow-400 gap-0.5">
                                                    {[...Array(5)].map((_, i) => (
                                                        <FaStar key={i} size={12} className={i < rating.rating ? 'fill-current' : 'text-slate-200'} />
                                                    ))}
                                                </div>
                                            </div>
                                            <p className="text-sm text-slate-600 font-medium leading-relaxed italic">"{rating.review || 'No written review provided.'}"</p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="bg-slate-50 p-10 rounded-[2rem] text-center border-2 border-dashed border-slate-100">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 text-slate-200 shadow-sm">
                                            <FaStar size={24} />
                                        </div>
                                        <p className="text-slate-400 font-bold text-sm">No ratings yet</p>
                                        <p className="text-[10px] text-slate-300 uppercase font-black tracking-widest mt-1">Complete jobs to build your reputation</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Floating Action Button for Edit */}
        {isOwnProfile && (
            <div className="fixed bottom-8 right-8 z-50">
                {isEditing ? (
                    <div className="flex flex-col gap-3">
                        <button onClick={handleSave} className="bg-emerald-600 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:bg-emerald-700 transition-all hover:scale-110">
                            <FaSave size={20} />
                        </button>
                        <button onClick={() => setIsEditing(false)} className="bg-red-500 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:bg-red-600 transition-all hover:scale-110">
                            <FaTimes size={20} />
                        </button>
                    </div>
                ) : (
                    <button onClick={() => setIsEditing(true)} className="bg-slate-900 text-white w-16 h-16 rounded-full shadow-2xl flex items-center justify-center hover:bg-slate-800 transition-all hover:scale-110 border-4 border-white">
                        <FaEdit size={24} />
                    </button>
                )}
            </div>
        )}
    </div>
  );
}

export default WorkerProfilePage;